import React, { useState } from 'react';
import { Box, Button, Popover, Stack, Typography } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// dayjs needs the customParseFormat plugin to parse `YYYY-MM`. Calling
// `extend` is idempotent so it's safe to invoke at module load.
dayjs.extend(customParseFormat);

// ---------------------------------------------------------------------------
// MonthRangePicker — month-only range picker
//
// Modelled after the analytics dialog's day-granularity `DateRangeSelector`
// but constrained to whole months. The trigger button shows the formatted
// range (e.g. "Jan 2026 – Jun 2026"); clicking it opens a popover with two
// month/year DatePickers (From / To) and a preset list. Selections are
// committed to the parent via `onChange` only when the user clicks Apply,
// so an aborted edit doesn't trigger a refetch upstream.
// ---------------------------------------------------------------------------

/**
 * Inclusive month range. Months are stored as `YYYY-MM` strings (e.g.
 * `'2026-01'`) so they're trivially serialisable into URL/query params
 * and don't carry day/time noise.
 */
export interface MonthRange {
  start: string;
  end: string;
}

export interface MonthRangePreset {
  id: string;
  label: string;
  build: () => MonthRange;
}

/** Build a range covering the last `n` calendar months (inclusive of the current month). */
export function lastNMonthsRange(n: number): MonthRange {
  const end = dayjs().startOf('month');
  const start = end.subtract(Math.max(1, n) - 1, 'month');
  return { start: start.format('YYYY-MM'), end: end.format('YYYY-MM') };
}

/** Convenience default used by pages that want "last 6 months" as their initial state. */
export const DEFAULT_MONTH_RANGE: MonthRange = lastNMonthsRange(6);

export const DEFAULT_MONTH_RANGE_PRESETS: MonthRangePreset[] = [
  { id: 'last3', label: 'Last 3 months', build: () => lastNMonthsRange(3) },
  { id: 'last6', label: 'Last 6 months', build: () => lastNMonthsRange(6) },
  { id: 'last12', label: 'Last 12 months', build: () => lastNMonthsRange(12) },
  {
    id: 'ytd',
    label: 'Year to date',
    build: () => {
      const end = dayjs().startOf('month');
      return {
        start: end.startOf('year').format('YYYY-MM'),
        end: end.format('YYYY-MM'),
      };
    },
  },
  {
    id: 'thisYear',
    label: 'This year',
    build: () => {
      const start = dayjs().startOf('year');
      return {
        start: start.format('YYYY-MM'),
        end: start.add(11, 'month').format('YYYY-MM'),
      };
    },
  },
];

function parseMonth(s: string): Dayjs | null {
  if (!s) return null;
  const d = dayjs(s, 'YYYY-MM', true);
  return d.isValid() ? d.startOf('month') : null;
}

function formatMonth(s: string): string {
  const d = parseMonth(s);
  return d ? d.format('MMM YYYY') : '';
}

/** Render a `MonthRange` as a human-readable label, collapsing single-month ranges. */
export function formatMonthRange(r: MonthRange): string {
  if (!r.start || !r.end) return 'Select range';
  if (r.start === r.end) return formatMonth(r.start);
  return `${formatMonth(r.start)} – ${formatMonth(r.end)}`;
}

export interface MonthRangePickerProps {
  value: MonthRange;
  onChange: (next: MonthRange) => void;
  /** Override the preset list. Defaults to `DEFAULT_MONTH_RANGE_PRESETS`. */
  presets?: MonthRangePreset[];
  /** Minimum width of the trigger button so the formatted range never clips. */
  buttonMinWidth?: number;
  /** Disable the trigger button (e.g. while a parent query is in flight). */
  disabled?: boolean;
  /**
   * Optional upper bound — months strictly after this are disabled. Defaults
   * to "this month" so users can't pick future months that wouldn't have data.
   */
  maxMonth?: string;
  /** Optional lower bound — months strictly before this are disabled. */
  minMonth?: string;
}

export default function MonthRangePicker({
  value,
  onChange,
  presets = DEFAULT_MONTH_RANGE_PRESETS,
  buttonMinWidth = 220,
  disabled = false,
  maxMonth,
  minMonth,
}: MonthRangePickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Draft state — mutated as the user picks months inside the popover and
  // only flushed to `onChange` on Apply.
  const [draftStart, setDraftStart] = useState<Dayjs | null>(null);
  const [draftEnd, setDraftEnd] = useState<Dayjs | null>(null);

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setDraftStart(parseMonth(value.start));
    setDraftEnd(parseMonth(value.end));
    setAnchor(e.currentTarget);
  };
  const closePopover = () => setAnchor(null);

  const maxDayjs = maxMonth ? parseMonth(maxMonth) : dayjs().startOf('month');
  const minDayjs = minMonth ? parseMonth(minMonth) : null;

  const applyDraft = () => {
    if (!draftStart || !draftEnd) return;
    // Auto-swap if the user picked the months in reverse order. Saves them
    // an extra interaction and prevents a malformed range from reaching
    // the parent.
    let s = draftStart.startOf('month');
    let e = draftEnd.startOf('month');
    if (e.isBefore(s)) [s, e] = [e, s];
    onChange({ start: s.format('YYYY-MM'), end: e.format('YYYY-MM') });
    closePopover();
  };

  const applyPreset = (preset: MonthRangePreset) => {
    const r = preset.build();
    setDraftStart(parseMonth(r.start));
    setDraftEnd(parseMonth(r.end));
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DateRangeIcon />}
        onClick={openPopover}
        disabled={disabled}
        sx={{
          flexShrink: 0,
          borderRadius: 2,
          textTransform: 'none',
          minWidth: buttonMinWidth,
          borderColor: 'var(--color-border-strong)',
          color: 'var(--color-text-strong)',
          bgcolor: 'var(--color-surface)',
          fontWeight: 600,
          justifyContent: 'flex-start',
          '&:hover': {
            borderColor: 'var(--color-accent)',
            bgcolor: 'var(--color-accent-soft)',
          },
        }}
      >
        {formatMonthRange(value)}
      </Button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              border: '1px solid var(--color-border)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: { xs: 320, sm: 420 } }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              sx={{ mb: 1.5 }}
            >
              <DatePicker
                label="From"
                views={['year', 'month']}
                openTo="month"
                value={draftStart}
                onChange={(v) => setDraftStart(v ? v.startOf('month') : null)}
                format="MMM YYYY"
                maxDate={maxDayjs ?? undefined}
                minDate={minDayjs ?? undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="To"
                views={['year', 'month']}
                openTo="month"
                value={draftEnd}
                onChange={(v) => setDraftEnd(v ? v.startOf('month') : null)}
                format="MMM YYYY"
                maxDate={maxDayjs ?? undefined}
                minDate={minDayjs ?? undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
          </LocalizationProvider>

          {presets.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 0.5 }}
              >
                Quick ranges
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {presets.map((p) => (
                  <Button
                    key={p.id}
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset(p)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.25,
                      fontSize: '0.75rem',
                      borderColor: 'var(--color-border-strong)',
                      color: 'var(--color-text-strong)',
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={closePopover} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={applyDraft}
              disabled={!draftStart || !draftEnd}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: 'var(--color-accent)',
                '&:hover': {
                  bgcolor: 'var(--color-accent-strong, var(--color-accent))',
                },
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
